/**
 * Referral capture + attribution helpers.
 *
 * A referral code arrives as `?ref=CODE` on any URL. Because sign-up runs
 * through Amplify's hosted Authenticator, there's no app code between "confirm
 * email" and the PostConfirmation Lambda that writes the profile — so we:
 *   1. capture the code at app startup and stash it (survives the user
 *      browsing the public site before they decide to sign up),
 *   2. thread it into sign-up as Cognito clientMetadata (primary attribution,
 *      handled atomically by PostConfirmation), and
 *   3. best-effort POST it after first sign-in as a fallback, in case the
 *      clientMetadata never reached the trigger.
 */

import { apiPost } from '../hooks/apiClient.js';

const REF_KEY = 'atlas_ref';
const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Read `?ref=` from the current URL, persist it, and strip it from the address
 * bar. Safe to call once at startup; a no-op when there's no ref param.
 */
export function captureRefFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const code = (params.get('ref') || '').trim();
    if (!code) return;

    localStorage.setItem(
      REF_KEY,
      JSON.stringify({ code: code.toUpperCase(), ts: Date.now() }),
    );

    // Clean the URL so the code doesn't linger or get accidentally re-shared.
    params.delete('ref');
    const qs = params.toString();
    const url = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    window.history.replaceState(null, '', url);
  } catch {
    // localStorage blocked (private mode) — referral simply won't attribute.
  }
}

/** The stored referral code, or null if none / expired. */
export function getStoredRef() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(REF_KEY);
    if (!raw) return null;
    const { code, ts } = JSON.parse(raw);
    if (!code || Date.now() - ts > REF_TTL_MS) {
      localStorage.removeItem(REF_KEY);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

export function clearStoredRef() {
  try {
    localStorage.removeItem(REF_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Fallback attribution. Safe to call on every sign-in: the server ignores it
 * for already-attributed or non-new accounts. Only clears the stored code once
 * the server has actually made a decision (a 2xx or a 4xx) — a transient /
 * credential error keeps the code so the next sign-in can retry.
 */
export async function attemptReferralClaim() {
  const code = getStoredRef();
  if (!code) return;
  try {
    await apiPost('/user/referral', { code });
    clearStoredRef();
  } catch (err) {
    if (err?.status && err.status >= 400 && err.status < 500) clearStoredRef();
  }
}
